import { typography, radius } from '@/shared/theme/tokens';
import { TERM } from '../liveModeMeta';

const MONO = typography.fontFamily.mono;
const LABEL = {
  fontFamily: MONO, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.wider,
  textTransform: 'uppercase',
};

/**
 * Top of the terminal: a thin system status line (identity, signal, freshness,
 * clock) over the adaptive broadcast headline — the one unambiguous state,
 * readable across the room.
 */
export function StatusBar({ deploymentName, clock, jitterActive, animate, headline, freshness }) {
  const time = clock.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const date = clock.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' }).toUpperCase();

  const signalColor = jitterActive ? TERM.warn : TERM.ok;
  const signalLabel = jitterActive ? 'DEMO' : 'LIVE';

  return (
    <div style={{ flexShrink: 0 }}>
      {/* System status line */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, height: 30, padding: '0 12px',
        background: TERM.raised, borderBottom: `1px solid ${TERM.line}`,
        color: TERM.inkDim, ...LABEL,
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: signalColor }}>
          <Dot color={signalColor} />
          {signalLabel}
        </span>
        <Sep />
        <span style={{ color: TERM.ink, letterSpacing: typography.letterSpacing.wide, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(deploymentName || 'CONTROL ROOM')}
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 14 }}>
          <Freshness freshness={freshness} />
          <Sep />
          <span style={{ color: TERM.ink, fontVariantNumeric: 'tabular-nums', letterSpacing: typography.letterSpacing.wide }}>{time}</span>
          <span style={{ color: TERM.inkFaint }}>{date}</span>
        </span>
      </div>

      {/* Adaptive broadcast headline */}
      {headline && <Headline headline={headline} animate={animate} />}
    </div>
  );
}

function Headline({ headline, animate }) {
  const pulses = headline.state === 'critical' || headline.state === 'degraded';
  return (
    <div style={{
      display: 'flex', alignItems: 'stretch', gap: 0,
      background: headline.bgDark, borderBottom: `1px solid ${TERM.line}`,
      minHeight: 76,
    }}>
      {/* Leading filled status block (contained — not a side-stripe accent) */}
      <div style={{
        flexShrink: 0, width: 76, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: headline.color, color: TERM.void,
        animation: animate && pulses ? 'lm-pulse 1.3s steps(1,end) infinite' : 'none',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 38 }}>{headline.icon}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '0 20px', flex: 1, minWidth: 0 }}>
        <span style={{
          fontFamily: MONO, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold,
          letterSpacing: typography.letterSpacing.wider, color: headline.color,
          border: `1px solid ${headline.border}`, padding: '3px 8px', flexShrink: 0,
        }}>
          {headline.code}
        </span>
        <span style={{
          fontFamily: MONO, fontSize: typography.fontSize['7xl'],
          fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wider,
          color: headline.color, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {headline.label}
        </span>
        {headline.count > 0 && (
          <span style={{
            fontFamily: MONO, fontSize: typography.fontSize['7xl'], fontWeight: typography.fontWeight.bold,
            color: headline.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1, flexShrink: 0,
          }}>
            ×{headline.count}
          </span>
        )}
        {headline.subtext && (
          <span style={{
            marginLeft: 'auto', fontFamily: MONO, fontSize: typography.fontSize.sm,
            letterSpacing: typography.letterSpacing.wide, color: TERM.inkFaint,
            fontVariantNumeric: 'tabular-nums', flexShrink: 0,
          }}>
            {headline.subtext}
          </span>
        )}
      </div>
    </div>
  );
}

function Freshness({ freshness }) {
  const { stale, ageSeconds } = freshness;
  const color = stale ? TERM.warn : TERM.ok;
  const ageLabel = ageSeconds < 2 ? 'NOW' : `${ageSeconds}s`;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color }}>
      <Dot color={color} />
      <span>{stale ? 'STALE' : 'DATA'}</span>
      <span style={{ color: TERM.inkFaint, fontVariantNumeric: 'tabular-nums' }}>{ageLabel}</span>
    </span>
  );
}

// A flat indicator LED — steady, not pulsing. The live clock + freshness age
// counter carry the honest "feed is updating" signal; the dot just shows standing.
function Dot({ color }) {
  return (
    <span style={{ width: 7, height: 7, borderRadius: radius.full, background: color, flexShrink: 0 }} />
  );
}

function Sep() {
  return <span style={{ color: TERM.line }}>│</span>;
}
